import React from 'react';
import { MapPin, Phone, Mail, Printer, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const LocationPage = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Pattaya Solicitors Office Location | Solicitors in Pattaya Thailand"
        description="Visit Pattaya Solicitors at South Pattaya Road. English speaking lawyers in Pattaya Thailand. Map, directions, opening hours. Law firm Pattaya for foreigners and expats."
        keywords="Pattaya Solicitors Location, Solicitors in Pattaya Thailand, Law Firm Pattaya for Foreigners, English Speaking Lawyer Pattaya Address, Pattaya Lawyers Thailand Office"
        path="/location"
        schemaType="location"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#000A22] via-[#001438] to-[#000A22] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-2 bg-[#F5E7D2] text-[#000A22] px-5 py-2.5 rounded-full font-medium text-sm">
              <MapPin className="w-4 h-4 text-[#D4A74A]" />
              Visit Our Office
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold">Pattaya Solicitors Office Location</h1>
            <p className="text-xl text-gray-300">Solicitors in Pattaya Thailand | English Speaking Lawyers</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 mb-16">
              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-bold text-[#000A22] mb-6">Contact Information</h2>
                  
                  <div className="space-y-6">
                    <Card className="border-2 hover:border-[#D4A74A] transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-[#F5E7D2] rounded-lg flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-6 h-6 text-[#D4A74A]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#000A22] mb-2">Office Address</h3>
                            <p className="text-gray-700 leading-relaxed">
                              20/173, Moo 10, South Pattaya Road,<br />
                              Pattaya City, Nongprue, Banglamung,<br />
                              Chonburi 20150<br />
                              Thailand
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-2 hover:border-[#D4A74A] transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-[#F5E7D2] rounded-lg flex items-center justify-center flex-shrink-0">
                            <Phone className="w-6 h-6 text-[#D4A74A]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#000A22] mb-2">Telephone</h3>
                            <div className="space-y-1">
                              <p>
                                <a href="tel:+6638412415" className="text-[#D4A74A] hover:underline">
                                  +66 (38) 412-415
                                </a>
                              </p>
                              <p>
                                <a href="tel:+66818383869" className="text-[#D4A74A] hover:underline">
                                  +66 (81) 838-3869
                                </a>
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-2 hover:border-[#D4A74A] transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-[#F5E7D2] rounded-lg flex items-center justify-center flex-shrink-0">
                            <Mail className="w-6 h-6 text-[#D4A74A]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#000A22] mb-2">Email</h3>
                            <p>
                              <a href="mailto:info@pattayasolicitors.com" className="text-[#D4A74A] hover:underline">
                                info@pattayasolicitors.com
                              </a>
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-2 hover:border-[#D4A74A] transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-[#F5E7D2] rounded-lg flex items-center justify-center flex-shrink-0">
                            <Clock className="w-6 h-6 text-[#D4A74A]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#000A22] mb-2">Office Hours</h3>
                            <p className="text-gray-700">Contact us for current office hours</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>

              {/* Location Map Image */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold text-[#000A22] mb-6">Find Us Here</h2>
                  <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-[#F5E7D2]">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_pattaya-solicitors-1/artifacts/2vsikpua_location-map.jpg"
                      alt="Pattaya solicitors office Thailand location map"
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="bg-[#F5E7D2] p-6 rounded-xl">
                  <h3 className="font-bold text-[#000A22] mb-3">Getting to Our Office</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our office is conveniently located on South Pattaya Road, easily accessible from all parts of Pattaya. We are situated near Tukcom IT Mall and along the main Sukhumvit Road corridor.
                  </p>
                </div>
              </div>
            </div>

            {/* Interactive Google Map */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#000A22] mb-6 text-center">Interactive Map</h2>
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-[#F5E7D2]">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4086.23577649107!2d100.87590727523875!3d12.923160415938177!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x310296129616d609%3A0x612a448c57e9a27f!2sDMC%20Inter%20Law%20%26%20Consultants!5e1!3m2!1sen!2sth!4v1771046648008!5m2!1sen!2sth" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }}
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="DMC Inter Law & Consultants Location"
                ></iframe>
              </div>
            </div>

            {/* Office Photo */}
            <div>
              <h2 className="text-3xl font-bold text-[#000A22] mb-6 text-center">Our Office</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_pattaya-solicitors-1/artifacts/l5og5k0p_meeting-room.jpg"
                    alt="Property lawyer Pattaya for condos consultation room"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_pattaya-solicitors-1/artifacts/elj3digp_attorney-desk.jpg"
                    alt="Company registration lawyer Pattaya office"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold text-[#000A22]">Visit Us Today</h2>
            <p className="text-xl text-gray-700">
              Walk-ins welcome, or call ahead to schedule a consultation at a time that suits you.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
